import { useMutation } from "@apollo/client/react";
import { App, Button, Checkbox, Flex, Space, Table, Tooltip, Typography } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  HistoryOutlined,
  SortDescendingOutlined,
  SwapOutlined,
} from "@ant-design/icons";

import { useRef, useState } from "react";
import { useEnterToNext } from "../../../hooks/useEnterToNext.ts";
import { useTranslation } from "react-i18next";
import {
  type GenericIngredientFragment,
  type IngredientFragment,
  IngredientType,
  type RecipeDetailFragment,
  RemoveRecipeIngredientDocument,
  ReplaceRecipeIngredientDocument,
  SortRecipeIngredientsByWeightDocument,
  UnitType,
  UpdateRecipeDocument,
  UpdateRecipeIngredientDocument,
  UpdateRecipeIngredientVersionDocument,
} from "../../../gql/generated/graphqlSchema.tsx";
import { RecipeIngredientNameCell } from "../RecipeIngredientNameCell.tsx";
import { QuantityUnitInput, type QuantityUnitValue } from "../../../components/QuantityUnitInput.tsx";
import { RecipeCostControls } from "../RecipeCostControls.tsx";
import { useRecipeCostColumn } from "../useRecipeCostColumn.tsx";
import { formatNumber } from "../../../utils/nutritionFormat.ts";
import { useSaving } from "../../../hooks/useSaving.tsx";
import { genericIngredientDisplayName } from "../../../utils/genericIngredient.ts";
import { CreateIngredientModal } from "../../ingredient/CreateIngredientModal.tsx";
import { useRecipe } from "../RecipeContext.tsx";
import { AddIngredientModal } from "./AddIngredientModal.tsx";
import { IngredientSearchModal, type SearchItem } from "./IngredientSearchModal.tsx";
import { noWrap } from "../../../styles/shared.styles.ts";

type IngredientRow = RecipeDetailFragment["ingredients"][number];

// t("recipes.ingredientTypes.IngredientRecipeIngredient")
// t("recipes.ingredientTypes.SubRecipeRecipeIngredient")

export const RecipeIngredientsTab = () => {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const { recipe, setRecipe, units } = useRecipe();
  const { trackSaving } = useSaving();
  const { showPrices, priceColumn, costTotalCell, costModals } = useRecipeCostColumn();
  const [updateIngredient] = useMutation(UpdateRecipeIngredientDocument);
  const [removeIngredient] = useMutation(RemoveRecipeIngredientDocument);
  const [updateRecipe] = useMutation(UpdateRecipeDocument);
  const [updateVersion] = useMutation(UpdateRecipeIngredientVersionDocument);
  const [sortByWeight] = useMutation(SortRecipeIngredientsByWeightDocument);
  const [replaceIngredient] = useMutation(ReplaceRecipeIngredientDocument);
  const tableRef = useRef<HTMLDivElement>(null);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const [createFromGeneric, setCreateFromGeneric] = useState<GenericIngredientFragment | null>(null);

  const ingredients = recipe.ingredients;

  const weightUnits = units.filter((u) => u.unitType !== UnitType.Volume);
  const gramsUnit = units.find((u) => u.code === "g");

  const unitsFor = (record: IngredientRow) => {
    const hasDensity = record.__typename === "IngredientRecipeIngredient" ? record.ingredient.density != null : false;
    return units.filter((u) => hasDensity || u.unitType !== UnitType.Volume);
  };

  const handleQuantityUnitChange =
    (record: IngredientRow) =>
    ({ quantity, unitId }: QuantityUnitValue) => {
      trackSaving(() =>
        updateIngredient({
          variables: {
            recipeVersionId: recipe.versionId,
            ingredientId: record.id,
            input: { quantity, unitId },
          },
        }).then((r) => setRecipe(r.data?.updateRecipeIngredient?.refreshedRecipe)),
      );
    };

  const handleUpdateVersion = (record: IngredientRow) => {
    trackSaving(() =>
      updateVersion({
        variables: { recipeVersionId: recipe.versionId, ingredientId: record.id },
      }).then((result) => {
        setRecipe(result.data?.updateRecipeIngredientVersion?.refreshedRecipe);
        message.success(t("recipes.updateIngredientVersionDone"));
      }),
    );
  };

  const handleDelete = (record: IngredientRow) => {
    modal.confirm({
      title: t("recipes.removeIngredientTitle"),
      content: t("recipes.removeIngredientConfirm"),
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      cancelButtonProps: { icon: <CloseOutlined /> },
      okText: t("recipes.removeIngredient"),
      onOk: () =>
        trackSaving(() =>
          removeIngredient({
            variables: { recipeVersionId: recipe.versionId, ingredientId: record.id },
          }).then((result) => {
            setRecipe(result.data?.removeRecipeIngredient?.refreshedRecipe);
            message.success(t("recipes.removeIngredientDone"));
          }),
        ),
    });
  };

  const handleReplace = (item: SearchItem) => {
    if (!replacingId) return;
    const targetId = replacingId;
    setReplacingId(null);
    trackSaving(async () => {
      const r = await replaceIngredient({
        variables: {
          recipeVersionId: recipe.versionId,
          ingredientId: targetId,
          input: {
            ingredientType: item.ingredientType,
            ingredientId: item.ingredientType === IngredientType.Ingredient ? item.id : undefined,
            subRecipeId: item.ingredientType === IngredientType.Recipe ? item.id : undefined,
          },
        },
      });
      setRecipe(r.data?.replaceRecipeIngredient?.refreshedRecipe);
      message.success(t("recipes.replaceIngredientDone"));
    });
  };

  const handleCreatedForReplace = (ingredient: IngredientFragment) => {
    setCreateFromGeneric(null);
    handleReplace({
      id: ingredient.id,
      name: ingredient.name,
      nutritionProfile: ingredient.nutritionProfile,
      ingredientType: IngredientType.Ingredient,
    });
  };

  const handleWeightAfterCookingChange = ({ quantity, unitId }: QuantityUnitValue) => {
    trackSaving(() =>
      updateRecipe({
        variables: {
          id: recipe.id,
          input: { weightAfterCookingQuantity: quantity, weightAfterCookingUnitId: unitId },
        },
      }).then((result) => setRecipe(result.data?.updateRecipe?.refreshedRecipe)),
    );
  };

  const enterToNext = useEnterToNext(tableRef);

  const totalRawWeight = ingredients.reduce((sum, ri) => sum + ri.quantityGrams, 0);
  const weightAfterCooking = recipe.weightAfterCooking;
  const yieldPercent =
    weightAfterCooking != null && totalRawWeight > 0 ? (weightAfterCooking / totalRawWeight) * 100 : null;

  return (
    <Flex gap="middle" vertical>
      {ingredients.length > 0 ? (
        <Space wrap>
          <AddIngredientModal />
          <Button
            color="primary"
            icon={<SortDescendingOutlined />}
            onClick={() =>
              trackSaving(() =>
                sortByWeight({
                  variables: { recipeVersionId: recipe.versionId },
                }).then((r) => {
                  setRecipe(r.data?.sortRecipeIngredientsByWeight?.refreshedRecipe);
                  message.success(t("recipes.sortByWeightDone"));
                }),
              )
            }
            variant="outlined"
          >
            {t("recipes.sortByWeight")}
          </Button>
          <RecipeCostControls />
        </Space>
      ) : null}

      <div ref={tableRef}>
        <Table<IngredientRow>
          bordered
          columns={[
            {
              title: "#",
              key: "id",
              width: 1,
              render: (_, __, index) => index + 1,
            },
            {
              title: t("recipes.ingredientName"),
              key: "name",
              width: "100%",
              render: (_, record) => <RecipeIngredientNameCell record={record} />,
            },
            {
              title: t("recipes.quantity"),
              key: "quantity",
              onHeaderCell: () => ({ colSpan: 3 }),
              render: (_, record) => (
                <QuantityUnitInput
                  inputClassName="marker"
                  onChange={handleQuantityUnitChange(record)}
                  onPressEnter={enterToNext}
                  units={unitsFor(record)}
                  value={{ quantity: record.quantity, unitId: record.unitId }}
                />
              ),
            },
            {
              key: "quantityGrams",
              align: "right",
              onHeaderCell: () => ({ colSpan: 0 }),
              render: (_, record) => (
                <span className={noWrap}>
                  {formatNumber(record.quantityGrams, i18n.language)} {gramsUnit?.code}
                </span>
              ),
            },
            {
              key: "quantityPercent",
              align: "right",
              onHeaderCell: () => ({ colSpan: 0 }),
              render: (_, record) => (
                <Tooltip destroyOnHidden title={t("recipes.showQuantityTooltip")}>
                  <Checkbox
                    checked={record.showQuantity}
                    onChange={(e) =>
                      trackSaving(() =>
                        updateIngredient({
                          variables: {
                            recipeVersionId: recipe.versionId,
                            ingredientId: record.id,
                            input: { showQuantity: e.target.checked },
                          },
                        }).then((r) => setRecipe(r.data?.updateRecipeIngredient?.refreshedRecipe)),
                      )
                    }
                  >
                    <span className={noWrap}>{formatNumber(record.quantityPercent, i18n.language, 1)}%</span>
                  </Checkbox>
                </Tooltip>
              ),
            },
            ...(showPrices ? [priceColumn] : []),
            {
              title: t("recipes.actions"),
              key: "actions",
              fixed: "right",
              render: (_, record) => (
                <Space>
                  <Tooltip destroyOnHidden title={t("recipes.replaceIngredient")}>
                    <Button icon={<SwapOutlined />} onClick={() => setReplacingId(record.id)} variant="outlined" />
                  </Tooltip>
                  <Tooltip
                    destroyOnHidden
                    title={t(record.isOutdated ? "recipes.updateIngredientVersion" : "recipes.ingredientUpToDate")}
                  >
                    <Button
                      color="yellow"
                      disabled={!record.isOutdated}
                      icon={<HistoryOutlined />}
                      onClick={() => handleUpdateVersion(record)}
                      variant="outlined"
                    />
                  </Tooltip>
                  <Tooltip destroyOnHidden title={t("recipes.removeIngredient")}>
                    <Button
                      color="danger"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(record)}
                      variant="outlined"
                    />
                  </Tooltip>
                </Space>
              ),
            },
          ]}
          dataSource={ingredients}
          locale={{
            emptyText: (
              <Flex align="center" gap="middle" vertical>
                <Typography.Text type="secondary">{t("recipes.noIngredients")}</Typography.Text>
                <AddIngredientModal />
              </Flex>
            ),
          }}
          pagination={false}
          rowKey="id"
          scroll={{ x: "max-content" }}
          size="small"
          sticky={{ offsetHeader: 0 }}
          // eslint-disable-next-line react/no-unstable-nested-components
          summary={() =>
            ingredients.length > 0 ? (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={3} index={0}>
                    <strong>{t("recipes.total")}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" index={3}>
                    <strong className={noWrap}>
                      {formatNumber(totalRawWeight, i18n.language)} {gramsUnit?.code}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                  {showPrices ? costTotalCell(5) : <Table.Summary.Cell index={5} />}
                  {showPrices ? <Table.Summary.Cell index={6} /> : null}
                </Table.Summary.Row>
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={2} index={0}>
                    <strong>{t("recipes.weightAfterCooking")}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <QuantityUnitInput
                      inputClassName="marker"
                      onChange={handleWeightAfterCookingChange}
                      units={weightUnits}
                      value={{
                        quantity: recipe.weightAfterCookingQuantity ?? null,
                        unitId: recipe.weightAfterCookingUnitId,
                      }}
                    />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" index={3}>
                    <strong className={noWrap}>
                      {weightAfterCooking != null
                        ? `${formatNumber(weightAfterCooking, i18n.language)} ${gramsUnit?.code}`
                        : null}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} />
                  {showPrices ? <Table.Summary.Cell index={6} /> : null}
                </Table.Summary.Row>
                {yieldPercent != null ? (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={2} index={0}>
                      <strong>{t("recipes.yield")}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                    <Table.Summary.Cell align="right" index={3}>
                      <strong className={noWrap}>{formatNumber(yieldPercent, i18n.language, 1)}%</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} />
                    <Table.Summary.Cell index={5} />
                    {showPrices ? <Table.Summary.Cell index={6} /> : null}
                  </Table.Summary.Row>
                ) : null}
              </>
            ) : null
          }
        />
      </div>
      <IngredientSearchModal
        actionIcon={<SwapOutlined />}
        actionLabel={t("recipes.replaceIngredient")}
        genericActionLabel={t("recipes.createAndReplace")}
        onClose={() => setReplacingId(null)}
        onCreateFromGeneric={(gi) => setCreateFromGeneric(gi)}
        onSelect={handleReplace}
        open={replacingId != null}
        title={t("recipes.replaceIngredientTitle")}
      />
      <CreateIngredientModal
        genericIngredient={createFromGeneric ?? undefined}
        initialName={createFromGeneric != null ? genericIngredientDisplayName(createFromGeneric, i18n.language) : ""}
        onClose={() => setCreateFromGeneric(null)}
        onCreated={handleCreatedForReplace}
        open={createFromGeneric != null}
      />
      {costModals}
    </Flex>
  );
};
